import { WEB_NAMESPACES, webBundledResources } from '@kiao-voice/core/i18n/web'
import i18next, { type i18n } from 'i18next'
import type { WebLocale } from './locale'

/**
 * Create a fresh i18next instance for the Next.js web app.
 *
 * The factory is called once per mount inside the client-side
 * `I18nProvider`, with the locale resolved on the server. This keeps
 * SSR and the first client render in sync — the same locale is used to
 * render HTML on the server and to hydrate on the client — so there is
 * no flash of English before the translated content appears.
 *
 * We intentionally do NOT mutate a module-level singleton: Next.js reuses
 * the same server process across requests, so mutating a shared i18next
 * instance during SSR would leak the locale of one request into the next.
 *
 * Only the `common` + `web` namespaces are bundled (via
 * `@kiao-voice/core/i18n/web`); the desktop + api namespaces never ship to
 * the browser. Both locales stay bundled so `changeLanguage` (zh-TW ↔ en)
 * works without a network round-trip.
 */
export function createWebI18n(locale: WebLocale): i18n {
  const instance = i18next.createInstance()

  instance.init({
    lng: locale,
    // Primary market is Taiwan. If a translation key is missing for the
    // user's locale (rare — both en + zh-TW are kept in lockstep), fall
    // back to zh-TW rather than English.
    fallbackLng: 'zh-TW',
    ns: WEB_NAMESPACES,
    // 'common' — the shared default namespace; it is part of WEB_NAMESPACES.
    defaultNS: WEB_NAMESPACES[0],
    resources: webBundledResources,
    interpolation: {
      escapeValue: false,
    },
  })

  return instance
}
